import {
    calculateMaxZoom,
    INDEX_LENGTH_U32,
    INDEXES_PER_TILE,
} from "plugins/ColorFinder/processing/SuperIndexController.class"
import { ColorFinderTiler } from "../ColorFinderTiler.class"
import { COLOR_BINS, rgb2hcb } from "./colorspace"

const MIN_PLOT_CHROMA = 0
const MAX_PLOT_CHROMA = 110
export const PLOT_SIZE = 214
// const PLOT_DECAY = 30 // frames either side of current.
const PLOT_SYMBOL_RADIUS = 2
const CUMULATIVE_PLOT_OPACITY = 0.25 * 255

export type ColorplotPointData = {
    x: number
    y: number
    rgb: number[]
}

// Call anytime to update the plot

// Call once to setup the plot
export function initializeColorplot(canvas: HTMLCanvasElement) {
    // Add svg to the div container
    canvas.width = PLOT_SIZE
    canvas.height = PLOT_SIZE
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const imageData = ctx.createImageData(canvas.width, canvas.height)

    // Draw circles on the polar plot
    const rgb: number[][] = []
    for (let r = 0; r < COLOR_BINS; r++) {
        const red = r * 8
        for (let g = 0; g < COLOR_BINS; g++) {
            const green = g * 8
            for (let b = 0; b < COLOR_BINS; b++) {
                const blue = b * 8
                rgb.push([red, green, blue])
            }
        }
    }

    ctx.putImageData(imageData, 0, 0)
}

export function initializeColorplotDrawable(canvas: HTMLCanvasElement) {
    canvas.width = PLOT_SIZE
    canvas.height = PLOT_SIZE
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
}

export function updateColorplot(
    tiler: ColorFinderTiler,
    cumulativeRgbPoints: Record<number, ColorplotPointData>,
    canvas: HTMLCanvasElement,
    position: number
) {
    const maxZoom = calculateMaxZoom(tiler.getNumFrames())
    const tile = tiler.getTileByHand(maxZoom, Math.floor(position / INDEXES_PER_TILE))
    if (!tile) return

    const localIndex = position % INDEXES_PER_TILE

    const start = localIndex * INDEX_LENGTH_U32
    const end = start + INDEX_LENGTH_U32

    const frameIndex = tile.subarray(start, end)

    if (!frameIndex) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    drawCumulativeRgbData(ctx, cumulativeRgbPoints)
    drawRgbData(ctx, frameIndex)
}

function drawCumulativeRgbData(
    ctx: CanvasRenderingContext2D,
    cumulativeRgb: Record<number, ColorplotPointData>
) {
    const opacity = CUMULATIVE_PLOT_OPACITY / 255

    for (const point of Object.values(cumulativeRgb)) {
        const [r, g, b] = point.rgb
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`
        ctx.beginPath()
        ctx.arc(point.x, point.y, PLOT_SYMBOL_RADIUS, 0, Math.PI * 2)
        ctx.fill()
    }
}

const { sin, cos, floor, min, max } = Math
const max_radius = PLOT_SIZE / 2 - PLOT_SYMBOL_RADIUS
function drawRgbData(ctx: CanvasRenderingContext2D, frameIndex: Uint32Array) {
    const brightest = new Map()

    for (let i = 0; i < frameIndex.length; i++) {
        let word = frameIndex[i]

        while (word) {
            const bit = 31 - Math.clz32(word)

            const color = (i << 5) + bit

            const red = (color >> 10) << 3
            const green = ((color >> 5) & 31) << 3
            const blue = (color & 31) << 3

            const [hue, chroma0, brightness] = rgb2hcb(color)
            const chroma = max(min(chroma0, MAX_PLOT_CHROMA), MIN_PLOT_CHROMA)

            const radius =
                ((chroma - MIN_PLOT_CHROMA) * max_radius) / (MAX_PLOT_CHROMA - MIN_PLOT_CHROMA)
            const x = floor(radius * cos(hue) + PLOT_SIZE / 2)
            const y = floor(-radius * sin(hue) + PLOT_SIZE / 2)
            const key = y * PLOT_SIZE + x

            if (brightness > (brightest.get(key)?.brightness ?? 0)) {
                brightest.set(key, { brightness, x, y, rgb: [red, green, blue] })
            }
            word ^= 1 << bit
        }
    }

    for (const point of brightest.values()) {
        const [r, g, b] = point.rgb
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
        ctx.beginPath()
        ctx.arc(point.x, point.y, PLOT_SYMBOL_RADIUS, 0, Math.PI * 2)
        ctx.fill()
    }
}

const colorsToKey = (red: number, green: number, blue: number) => {
    const key = (red >> 3) * 1024 + (green >> 3) * 32 + (blue >> 3)
    return key
}

/**
 * This should be called by rgbCumulative() only.
 * @returns Entries of ColorplotPointData, keyed by `PLOT_SIZE * x + y`
 */
export function cumulativeRgbPlotData(cumulativeRgbData: number[][]) {
    const { sin, cos, floor, min, max } = Math
    const max_radius = PLOT_SIZE / 2 - PLOT_SYMBOL_RADIUS
    const colorplotPoints: Record<number, ColorplotPointData> = {}
    const allPoints = new Set<ColorplotPointData>()

    const brightest = new Map()
    for (const [red, green, blue] of cumulativeRgbData) {
        const colorKey = colorsToKey(red, green, blue)
        const [hue, chroma0, brightness] = rgb2hcb(colorKey)
        const chroma = max(min(chroma0, MAX_PLOT_CHROMA), MIN_PLOT_CHROMA)

        const radius =
            ((chroma - MIN_PLOT_CHROMA) * max_radius) / (MAX_PLOT_CHROMA - MIN_PLOT_CHROMA)

        const x = floor(radius * cos(hue) + PLOT_SIZE / 2)
        const y = floor(-radius * sin(hue) + PLOT_SIZE / 2)
        const key = y * PLOT_SIZE + x

        const pointData = { x, y, rgb: [red, green, blue] }
        if (brightness > (brightest.get(key) ?? 0)) {
            brightest.set(key, brightness)

            // Create base-640 key
            const pointKey = PLOT_SIZE * x + y
            colorplotPoints[pointKey] = pointData
        }
        allPoints.add(pointData)
    }

    return { colorplotPoints, allPoints }
}

/**
 * @returns The RGB data that is displayed at a given point on the cumulative colorplot.
 */
export function cumulativeRgbDataAtPoint(
    x: number,
    y: number,
    cumulativeRgbData: Record<number, ColorplotPointData>
): number[] | undefined {
    const pointKey = PLOT_SIZE * x + y
    return cumulativeRgbData[pointKey]?.rgb
}
